import { useState, useEffect } from 'react';
import { User, USERS, RESIDENT_FALLBACKS } from '../types/auth';
import { UserRound, Lock } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

interface LoginPageProps {
  onLogin: (user: User) => void;
}

// Gunakan tipe bawaan React untuk event form
type FormEvent = React.FormEvent<HTMLFormElement>;

export function LoginPage({ onLogin }: LoginPageProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [time, setTime] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('id-ID', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const u = username.trim();
    const p = password.trim();
    if (!u || !p) {
      setError('Username dan password wajib diisi');
      setIsLoading(false);
      return;
    }
    try {
      // Supabase first
      if (isSupabaseConfigured) {
        const { data: userData, error: sbError } = await supabase
          .from('app_users')
          .select('id, username, role, room_number, password_plain')
          .eq('username', u)
          .eq('password_plain', p)
          .single();
        if (userData && !sbError) {
          let accessLevel: 1 | 2 | 3 = 3;
          if (userData.role === 'admin') accessLevel = 1;
          else if (userData.role === 'manager' || userData.role === 'user') accessLevel = 2;
          else if (userData.role === 'resident') accessLevel = 3;
          const userObj = { id: userData.id, username: userData.username, accessLevel, role: userData.role, roomNumber: userData.room_number };
          localStorage.setItem('currentUser', JSON.stringify(userObj));
          void supabase.from('app_users').update({ last_login_at: new Date().toISOString() }).eq('id', userData.id);
          void supabase.from('audit_logs').insert({ username: userData.username, action: 'login', result: 'success', details: { id: userData.id, role: userData.role } });
          onLogin(userObj);
          return;
        }
      }
      // Legacy admin/users fallback
      const legacy = USERS.find(u0 => u0.username === u && (u0 as any).password === p);
      if (legacy) {
        const userData = { id: legacy.id, username: legacy.username, accessLevel: legacy.accessLevel, role: 'admin' as const };
        localStorage.setItem('currentUser', JSON.stringify(userData));
        if (isSupabaseConfigured) void supabase.from('audit_logs').insert({ username: legacy.username, action: 'login', result: 'success', details: { id: legacy.id, legacy: true } });
        onLogin(userData);
        return;
      }
      // Resident fallback offline list
      const resident = RESIDENT_FALLBACKS.find(r => r.username === u && r.password === p);
      if (resident) {
        const userObj: User = { id: resident.username, username: resident.username, accessLevel: 3, role: 'resident', roomNumber: resident.roomNumber };
        localStorage.setItem('currentUser', JSON.stringify(userObj));
        if (isSupabaseConfigured) void supabase.from('audit_logs').insert({ username: resident.username, action: 'login', result: 'success', details: { fallback: true } });
        onLogin(userObj);
        return;
      }
      setError('Username atau password salah / belum terdaftar');
      setIsLoading(false);
    } catch (err) {
      console.error('Login error:', err);
      // Try resident fallback even if error thrown earlier (network etc.)
      const resident = RESIDENT_FALLBACKS.find(r => r.username === u && r.password === p);
      if (resident) {
        const userObj: User = { id: resident.username, username: resident.username, accessLevel: 3, role: 'resident', roomNumber: resident.roomNumber };
        localStorage.setItem('currentUser', JSON.stringify(userObj));
        onLogin(userObj);
        return;
      }
      setError('Terjadi kesalahan saat login');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center px-4 py-8 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950">
      <div className="w-full max-w-md">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-primary-600 rounded-2xl border-4 border-primary-700 shadow-lg mb-4">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">deKost SRI 2</h1>
          <p className="text-lg text-slate-300">Sistem Manajemen Kosan</p>
        </div>

        {/* Login Card - Solid Design */}
        <div className="login-card p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-light-text dark:text-dark-text mb-2">Selamat Datang</h2>
            <p className="text-base text-light-textSecondary dark:text-dark-textSecondary">Silahkan masuk untuk melanjutkan</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-base font-semibold text-light-text dark:text-dark-text mb-2">
                Username
              </label>
              <div className="flex items-center gap-3 bg-light-surface dark:bg-dark-surface border-2 border-light-border dark:border-dark-border rounded-xl px-4 py-3 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20">
                <UserRound className="h-6 w-6 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e: any) => setUsername(e.target.value)}
                  className="flex-1 bg-transparent text-lg text-light-text dark:text-dark-text placeholder:text-light-textMuted dark:placeholder:text-dark-textMuted focus:outline-none"
                  placeholder="Masukkan username"
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-base font-semibold text-light-text dark:text-dark-text mb-2">
                Password
              </label>
              <div className="flex items-center gap-3 bg-light-surface dark:bg-dark-surface border-2 border-light-border dark:border-dark-border rounded-xl px-4 py-3 focus-within:border-accent-500 focus-within:ring-2 focus-within:ring-accent-500/20">
                <Lock className="h-6 w-6 text-accent-600 dark:text-accent-400 flex-shrink-0" />
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e: any) => setPassword(e.target.value)}
                  className="flex-1 bg-transparent text-lg text-light-text dark:text-dark-text placeholder:text-light-textMuted dark:placeholder:text-dark-textMuted focus:outline-none"
                  placeholder="Masukkan password"
                  autoComplete="current-password"
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-error-50 dark:bg-error-900/30 border-2 border-error-200 dark:border-error-700 rounded-xl px-4 py-3">
                <p className="text-base font-semibold text-error-700 dark:text-error-300 text-center">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full text-lg"
            >
              {isLoading ? 'Memproses...' : 'Masuk'}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t-2 border-light-border dark:border-dark-border text-center">
            <div className="inline-flex items-center gap-2 bg-slate-100 dark:bg-slate-700 px-4 py-2 rounded-lg mb-3">
              <span className="text-lg font-mono font-bold text-slate-700 dark:text-slate-300">{formatTime(time)}</span>
            </div>
            <p className="text-sm text-light-textMuted dark:text-dark-textMuted">
              © 2024-2026 Irfan Ananda
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
